import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Hash, RefreshCw, Loader2, CheckCircle2, Undo2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/apiErrors';

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-lg border bg-muted/30 p-3" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold font-mono ${accent || ''}`}>{value ?? '—'}</p>
  </div>
);

export const ProntuariosMigracao = ({ api }) => {
  const [data, setData] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [rollbackId, setRollbackId] = useState(null);
  const [rollbackConfirm, setRollbackConfirm] = useState('');
  const [resultado, setResultado] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([api.get('/prontuarios/migracao/preview'), api.get('/prontuarios/migracao/historico')]);
      setData(p.data);
      setHistorico(h.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Erro ao carregar prévia da numeração'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const aplicar = async () => {
    setApplying(true);
    try {
      const r = await api.post('/prontuarios/migracao/aplicar', { vincular_docs: true, confirmacao: confirm });
      setResultado(r.data);
      toast.success(r.data.numeradas ? `${r.data.numeradas} internações numeradas` : r.data.mensagem);
      setOpen(false); setConfirm('');
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Erro ao aplicar numeração'));
    } finally {
      setApplying(false);
    }
  };

  const reverter = async () => {
    setApplying(true);
    try {
      const r = await api.post('/prontuarios/migracao/rollback', { migration_id: rollbackId, confirmacao: rollbackConfirm });
      toast.success(`${r.data.revertidas} internações revertidas`);
      setRollbackId(null); setRollbackConfirm(''); setResultado(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Erro ao reverter'));
    } finally {
      setApplying(false);
    }
  };

  const lev = data?.levantamento;

  return (
    <Card data-testid="prontuarios-migracao-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Hash className="h-5 w-5" />Numeração de Prontuários</CardTitle>
        <CardDescription>
          Numera internações históricas sem prontuário em ordem cronológica de admissão (mais antiga = menor número). Novas admissões continuam a partir do último número. Operação idempotente, auditada e reversível.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="prontuarios-recarregar-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Atualizar prévia
          </Button>
          {lev && (lev.sem_numero > 0
            ? <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="prontuarios-status-badge">{lev.sem_numero} sem número</Badge>
            : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="prontuarios-status-badge"><CheckCircle2 className="h-3 w-3 mr-1" />Todas numeradas</Badge>)}
        </div>

        {lev && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Pacientes únicos" value={lev.pacientes_unicos} testid="stat-pacientes" />
            <Stat label="Internações" value={lev.internacoes} testid="stat-internacoes" />
            <Stat label="Readmitidos" value={lev.pacientes_readmitidos} testid="stat-readmitidos" />
            <Stat label="Excluídas (lógico)" value={lev.excluidas_logicamente} testid="stat-excluidas" />
            <Stat label="Já numeradas" value={lev.ja_numeradas} testid="stat-numeradas" />
            <Stat label="Sem número" value={lev.sem_numero} testid="stat-sem-numero" accent={lev.sem_numero > 0 ? 'text-amber-700' : 'text-emerald-700'} />
            <Stat label="Primeiro / Último" value={lev.primeiro_numero ? `${lev.primeiro_numero} / ${lev.ultimo_numero}` : '—'} testid="stat-faixa" />
            <Stat label="Próximo número" value={lev.proximo_numero} testid="stat-proximo" accent="text-blue-700" />
          </div>
        )}

        {lev?.sem_numero > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2" data-testid="prontuarios-aviso-pendente">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Ao aplicar, as {lev.sem_numero} internações pendentes receberão os números <b className="font-mono">{lev.primeiro_a_numerar}</b> a <b className="font-mono">{lev.ultimo_a_numerar}</b>. {lev.docs_sem_internacao > 0 && `${lev.docs_sem_internacao} documento(s) sem internação serão vinculados automaticamente quando houver uma única internação compatível.`}</span>
          </div>
        )}

        <Button onClick={() => setOpen(true)} disabled={!data?.pode_aplicar || loading} data-testid="prontuarios-aplicar-btn">
          <Hash className="h-4 w-4 mr-2" />Aplicar numeração histórica
        </Button>

        {resultado && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="prontuarios-resultado">
            <p className="font-medium text-emerald-900">Numeração aplicada {resultado.migration_id && <span className="font-mono text-xs">({resultado.migration_id})</span>}</p>
            <p className="text-emerald-800">Numeradas: <b data-testid="resultado-numeradas">{resultado.numeradas}</b> · Documentos vinculados: {resultado.docs_vinculados} · A revisar: {resultado.docs_revisar} · Faixa: <span className="font-mono">{resultado.depois?.primeiro_numero} – {resultado.depois?.ultimo_numero}</span> · Próximo: <span className="font-mono">{resultado.depois?.proximo_numero}</span></p>
          </div>
        )}

        {historico.length > 0 && (
          <div className="space-y-2" data-testid="prontuarios-historico">
            <Label className="text-xs text-muted-foreground">Histórico de execuções</Label>
            {historico.map(m => (
              <div key={m.migration_id} className="flex items-center justify-between rounded border p-2 text-xs" data-testid={`hist-migracao-${m.migration_id}`}>
                <div>
                  <span className="font-mono">{m.migration_id}</span> · {new Date(m.em).toLocaleString('pt-BR')} · {m.depois?.ja_numeradas - (m.antes?.ja_numeradas || 0)} numeradas · docs {m.docs_vinculados}/{m.docs_revisar}
                  {m.usuario_email && <span className="text-muted-foreground"> · {m.usuario_email}</span>}
                  {m.revertida_em && <Badge variant="outline" className="ml-2 text-rose-700 border-rose-300">revertida</Badge>}
                </div>
                {!m.revertida_em && (
                  <Button variant="ghost" size="sm" className="text-rose-700" onClick={() => setRollbackId(m.migration_id)} data-testid={`rollback-btn-${m.migration_id}`}>
                    <Undo2 className="h-3 w-3 mr-1" />Reverter
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(''); }}>
        <DialogContent data-testid="prontuarios-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar numeração histórica</DialogTitle>
            <DialogDescription>
              {lev?.sem_numero} internações receberão números de <b className="font-mono">{lev?.primeiro_a_numerar}</b> a <b className="font-mono">{lev?.ultimo_a_numerar}</b>, por ordem de data de admissão. Números já existentes são preservados. A operação fica registrada e pode ser revertida.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-numerar">Digite <b>NUMERAR</b> para confirmar</Label>
            <Input id="confirm-numerar" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="prontuarios-confirm-input" autoComplete="off" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="prontuarios-cancel-btn">Cancelar</Button>
            <Button onClick={aplicar} disabled={confirm.trim().toUpperCase() !== 'NUMERAR' || applying} data-testid="prontuarios-confirm-btn">
              {applying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rollbackId} onOpenChange={(o) => { if (!o) { setRollbackId(null); setRollbackConfirm(''); } }}>
        <DialogContent data-testid="prontuarios-rollback-dialog">
          <DialogHeader>
            <DialogTitle>Reverter numeração</DialogTitle>
            <DialogDescription>Remove os números atribuídos pela execução <span className="font-mono">{rollbackId}</span> e desfaz vínculos automáticos de documentos. Números gerados em novas admissões não são afetados.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-reverter">Digite <b>REVERTER</b> para confirmar</Label>
            <Input id="confirm-reverter" value={rollbackConfirm} onChange={(e) => setRollbackConfirm(e.target.value)} data-testid="prontuarios-rollback-input" autoComplete="off" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackId(null)} data-testid="prontuarios-rollback-cancel-btn">Cancelar</Button>
            <Button variant="destructive" onClick={reverter} disabled={rollbackConfirm.trim().toUpperCase() !== 'REVERTER' || applying} data-testid="prontuarios-rollback-confirm-btn">
              {applying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Reverter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
